/**
 * AssignInvestorModal — single-action UI for villa↔investor assignment.
 *
 * Wraps POST /api/villas/{villa_id}/assign-investor with explicit confirmation
 * UX for the two destructive cases (force_reassign, replace_manual).
 *
 * Props:
 *   - villa: { villa_id, name, price_eur, owner_user_id }
 *   - onClose: () => void
 *   - onAssigned: (response) => void
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { X, UserPlus, Coin, Warning, CheckCircle, Info } from "@phosphor-icons/react";

const PCTS = [
  { step: 1, pct: 0.10, label: "Reserva" },
  { step: 2, pct: 0.40, label: "Segundo pago" },
  { step: 3, pct: 0.30, label: "Tercer pago" },
  { step: 4, pct: 0.20, label: "Entrega de llaves" },
];

export default function AssignInvestorModal({ villa, onClose, onAssigned }) {
  const [investors, setInvestors] = useState([]);
  const [planSummary, setPlanSummary] = useState(null);
  const [selectedInv, setSelectedInv] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [conflict, setConflict] = useState(null);
  const [forceReassign, setForceReassign] = useState(false);
  const [replaceManual, setReplaceManual] = useState(false);

  // Load investors + current plan in parallel
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/users"),
      api.get(`/villas/${villa.villa_id}/payment-plan`),
    ]).then(([usersRes, planRes]) => {
      if (cancelled) return;
      const invs = (usersRes.data || []).filter(u => u.role === "investor");
      setInvestors(invs);
      setPlanSummary(planRes.data);
      // If villa already has an owner that's an investor, preselect them.
      if (planRes.data?.owner_user_id) {
        setSelectedInv(planRes.data.owner_user_id);
      }
    }).catch(e => setErr(formatError(e)));
    return () => { cancelled = true; };
  }, [villa.villa_id]);

  const computedPlan = useMemo(() => {
    if (!villa.price_eur) return [];
    return PCTS.map(s => ({
      ...s,
      amount: Math.round(villa.price_eur * s.pct),
    }));
  }, [villa.price_eur]);

  const computedTotal = computedPlan.reduce((a, b) => a + b.amount, 0);
  const currentInvestor = planSummary?.investor;
  const reassigningToOther =
    planSummary?.owner_user_id &&
    selectedInv &&
    selectedInv !== planSummary.owner_user_id;
  const hasManual = (planSummary?.manual_payments || []).length > 0;

  const submit = async () => {
    if (!selectedInv) { setErr("Selecciona un inversor."); return; }
    setBusy(true); setErr(""); setConflict(null);
    try {
      const { data } = await api.post(`/villas/${villa.villa_id}/assign-investor`, {
        user_id: selectedInv,
        force_reassign: forceReassign || undefined,
        replace_manual: replaceManual || undefined,
      });
      onAssigned(data);
    } catch (e) {
      // 409 may include a structured detail (code, message)
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && typeof detail === "object") {
        setConflict(detail);
      } else {
        setErr(formatError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="assign-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 pb-3 border-b border-stone-200 sticky top-0 bg-white">
          <div>
            <div className="font-serif-dv text-2xl">Asignar inversor</div>
            <div className="text-sm text-stone-500 mt-0.5">
              {villa.name} · €{Number(villa.price_eur).toLocaleString("es-ES")}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
            aria-label="Cerrar"
            data-testid="assign-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Current state */}
          {planSummary && currentInvestor && (
            <div className="text-sm bg-stone-50 border border-stone-200 rounded p-3">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Estado actual</div>
              <div>
                Inversor: <span className="font-medium">{currentInvestor.name}</span>{" "}
                <span className="text-stone-400">({currentInvestor.email})</span>
              </div>
              <div className="text-xs text-stone-500 mt-1">
                {planSummary.has_full_auto_plan
                  ? `Plan automático completo (4 pagos)`
                  : `${(planSummary.auto_payments || []).length} pagos automáticos · ${(planSummary.manual_payments || []).length} manuales`}
              </div>
            </div>
          )}

          {/* Investor picker */}
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Inversor</label>
            <select
              className="dv-input"
              value={selectedInv}
              onChange={e => { setSelectedInv(e.target.value); setConflict(null); }}
              disabled={busy}
              data-testid="assign-investor-select"
            >
              <option value="">— Seleccionar inversor —</option>
              {investors.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name} · {u.email}
                </option>
              ))}
            </select>
            {investors.length === 0 && (
              <div className="text-xs text-stone-400 mt-1">No hay inversores creados todavía.</div>
            )}
          </div>

          {/* Plan preview */}
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-1.5">
              <Coin size={12} weight="bold" /> Plan de pagos a generar
            </div>
            <table className="w-full text-sm border border-stone-200 rounded overflow-hidden">
              <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Hito</th>
                  <th className="text-right px-3 py-2">%</th>
                  <th className="text-right px-3 py-2">Importe</th>
                </tr>
              </thead>
              <tbody data-testid="assign-plan-preview">
                {computedPlan.map(s => (
                  <tr key={s.step} className="border-t border-stone-100">
                    <td className="px-3 py-2 text-stone-400">P{s.step}</td>
                    <td className="px-3 py-2">{s.label}</td>
                    <td className="px-3 py-2 text-right text-stone-500">{(s.pct * 100).toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right font-medium">
                      €{s.amount.toLocaleString("es-ES")}
                    </td>
                  </tr>
                ))}
                <tr className="border-t border-stone-200 bg-stone-50">
                  <td colSpan={3} className="px-3 py-2 text-xs uppercase tracking-wider text-stone-500">Total</td>
                  <td className="px-3 py-2 text-right font-medium">
                    €{computedTotal.toLocaleString("es-ES")}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Reassign confirmation */}
          {reassigningToOther && (
            <ConfirmCheckbox
              testid="assign-confirm-reassign"
              tone="amber"
              checked={forceReassign}
              onChange={setForceReassign}
              icon={<Warning size={14} weight="bold" />}
              title="Reasignar a otro inversor"
              body={`Esta villa está asignada a ${currentInvestor?.name}. Al confirmar, los pagos automáticos previos se archivarán y se generará un nuevo plan para el inversor seleccionado.`}
            />
          )}

          {/* Manual payments confirmation */}
          {hasManual && (
            <ConfirmCheckbox
              testid="assign-confirm-replace"
              tone="amber"
              checked={replaceManual}
              onChange={setReplaceManual}
              icon={<Warning size={14} weight="bold" />}
              title="Archivar pagos manuales existentes"
              body={`Esta villa tiene ${(planSummary?.manual_payments || []).length} pago(s) creado(s) manualmente. Al confirmar, se archivarán antes de generar el plan automático.`}
            />
          )}

          {/* Server-side conflicts (when present) */}
          {conflict && (
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm" data-testid="assign-conflict">
              <div className="flex items-start gap-2">
                <Warning size={14} weight="bold" className="text-amber-700 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-amber-900">Confirmación necesaria</div>
                  <div className="text-amber-800 mt-0.5">{conflict.message}</div>
                  <div className="text-xs text-amber-700 mt-2">
                    Activa la casilla correspondiente arriba y pulsa "Asignar y generar pagos" otra vez.
                  </div>
                </div>
              </div>
            </div>
          )}

          {err && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-3">{err}</div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-6 pt-0">
          <button onClick={onClose} className="dv-btn dv-btn-ghost" data-testid="assign-cancel">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy || !selectedInv}
            className="dv-btn text-white inline-flex items-center gap-2 disabled:opacity-50"
            style={{ background: "#9C5236" }}
            data-testid="assign-submit"
          >
            <UserPlus size={14} weight="bold" />
            {busy ? "Asignando…" : "Asignar y generar pagos"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmCheckbox({ tone, checked, onChange, icon, title, body, testid }) {
  const toneCls = tone === "amber"
    ? "bg-amber-50 border-amber-300 text-amber-900"
    : "bg-red-50 border-red-200 text-red-900";
  return (
    <label
      className={`block border rounded p-3 text-sm cursor-pointer ${toneCls}`}
      data-testid={testid}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          className="mt-1"
        />
        <div className="flex-1">
          <div className="font-medium flex items-center gap-1.5">{icon} {title}</div>
          <div className="text-xs mt-0.5 opacity-90">{body}</div>
        </div>
      </div>
    </label>
  );
}
